// ═══════════════════════════════════════════════════════════════════════════
// MFI Progression Trend Classifier
// ───────────────────────────────────────────────────────────────────────────
// Converts a series of Missing Fish Intelligence runs (`mfi_history`) into a
// single trend label the user can act on at a glance.
//
// DESIGN PRINCIPLES:
//   1. Low-confidence runs must NOT distort the trend. They are weighted to
//      zero so a single MONITOR_ONLY run cannot swing the label.
//   2. At least two usable (non-zero weight) runs are required — otherwise
//      the output is "Uncertain".
//   3. Relative-change band keeps small fluctuations from being flagged as
//      movement (default ±10% or ±250 fish absolute, whichever is larger).
//   4. Trend classification never implies a stock decision — that remains
//      gated by `resolveDecisionState()` elsewhere.
// ═══════════════════════════════════════════════════════════════════════════

export const MFI_TREND_THRESHOLDS = Object.freeze({
  STABLE_BAND_PCT: 10,          // ±10% counts as Stable
  STABLE_BAND_ABSOLUTE: 250,    // ±250 fish counts as Stable even when relative is higher
  MIN_USABLE_RUNS: 2,           // Need at least 2 confidence-weighted runs
  TREND_CONFIDENCE_HIGH: 4,     // ≥ 4 usable runs → High
  TREND_CONFIDENCE_MODERATE: 2, // 2–3 usable runs → Moderate
});

/**
 * Confidence weight for a single history entry.
 * MONITOR_ONLY / Indication Only / Low strength → 0 (excluded).
 * Moderate / evidence mode → 0.5.
 * High / decision mode → 1.0.
 * @param {Object} entry  mfi_history row
 * @returns {number} 0..1
 */
function weightForEntry(entry) {
  const strength = (entry?.detection_strength || '').toLowerCase();
  const mode = (entry?.confidence_mode || '').toLowerCase();
  const actionability = (entry?.actionability || '').toUpperCase();

  if (actionability === 'MONITOR_ONLY') return 0;
  if (strength === 'low' || strength === 'indication only') return 0;
  if (mode === 'signal') return 0;

  if (strength === 'high' || mode === 'decision') return 1;
  if (strength === 'moderate' || mode === 'evidence') return 0.5;

  // Unknown / legacy entries default to low-weight so they don't swing the trend.
  return 0.25;
}

/**
 * Weighted mean of a slice of entries.
 * Returns null if the total weight is zero.
 */
function weightedMean(entries) {
  let totalWeight = 0;
  let totalValue = 0;
  for (const e of entries) {
    const w = e._weight;
    if (w <= 0) continue;
    const v = Number(e.estimated_missing) || 0;
    totalValue += v * w;
    totalWeight += w;
  }
  if (totalWeight <= 0) return null;
  return totalValue / totalWeight;
}

/**
 * Classify the trend across mfi_history.
 * @param {Array<Object>} mfiHistory
 * @returns {{
 *   trend: 'Increasing'|'Decreasing'|'Stable'|'Uncertain',
 *   confidence: 'High'|'Moderate'|'Low',
 *   usableCount: number,
 *   totalCount: number,
 *   formerAvg: number|null,
 *   latterAvg: number|null,
 *   deltaAbsolute: number|null,
 *   deltaPercent: number|null,
 *   reason: string,
 * }}
 */
export function classifyMfiTrend(mfiHistory) {
  const totalCount = Array.isArray(mfiHistory) ? mfiHistory.length : 0;
  const empty = {
    trend: 'Uncertain',
    confidence: 'Low',
    usableCount: 0,
    totalCount,
    formerAvg: null,
    latterAvg: null,
    deltaAbsolute: null,
    deltaPercent: null,
    reason: 'Not enough confidence-weighted runs to classify a trend.',
  };
  if (totalCount === 0) return empty;

  // Attach weights, keep chronological order (mfi_history is already append-ordered).
  const weighted = mfiHistory.map(e => ({ ...e, _weight: weightForEntry(e) }));
  const usable = weighted.filter(e => e._weight > 0);

  if (usable.length < MFI_TREND_THRESHOLDS.MIN_USABLE_RUNS) {
    return {
      ...empty,
      usableCount: usable.length,
      reason: usable.length === 0
        ? 'All runs have low confidence (MONITOR_ONLY / Indication Only). Trend cannot be established.'
        : 'Only one confidence-weighted run available. Need at least two to determine a trend direction.',
    };
  }

  // Split usable set into first and second halves. For odd counts, the middle
  // run joins the second half (biases the comparison toward recent movement,
  // which is what operators care about).
  const mid = Math.floor(usable.length / 2);
  const former = usable.slice(0, mid);
  const latter = usable.slice(mid);
  const formerAvg = weightedMean(former);
  const latterAvg = weightedMean(latter);

  if (formerAvg == null || latterAvg == null) {
    return { ...empty, usableCount: usable.length, reason: 'Could not compute weighted averages.' };
  }

  const deltaAbsolute = latterAvg - formerAvg;
  const base = Math.max(Math.abs(formerAvg), 1);
  const deltaPercent = (deltaAbsolute / base) * 100;

  const withinPctBand = Math.abs(deltaPercent) <= MFI_TREND_THRESHOLDS.STABLE_BAND_PCT;
  const withinAbsBand = Math.abs(deltaAbsolute) <= MFI_TREND_THRESHOLDS.STABLE_BAND_ABSOLUTE;
  const isStable = withinPctBand || withinAbsBand;

  let trend;
  let reason;
  if (isStable) {
    trend = 'Stable';
    reason = `Recent weighted average (${Math.round(latterAvg).toLocaleString()}) is within ±${MFI_TREND_THRESHOLDS.STABLE_BAND_PCT}% (or ±${MFI_TREND_THRESHOLDS.STABLE_BAND_ABSOLUTE} fish) of earlier runs — no material movement.`;
  } else if (deltaAbsolute > 0) {
    trend = 'Increasing';
    reason = `Recent weighted average (${Math.round(latterAvg).toLocaleString()}) is ${Math.round(deltaPercent)}% higher than earlier runs (${Math.round(formerAvg).toLocaleString()}).`;
  } else {
    trend = 'Decreasing';
    reason = `Recent weighted average (${Math.round(latterAvg).toLocaleString()}) is ${Math.abs(Math.round(deltaPercent))}% lower than earlier runs (${Math.round(formerAvg).toLocaleString()}).`;
  }

  let confidence;
  if (usable.length >= MFI_TREND_THRESHOLDS.TREND_CONFIDENCE_HIGH) confidence = 'High';
  else if (usable.length >= MFI_TREND_THRESHOLDS.TREND_CONFIDENCE_MODERATE) confidence = 'Moderate';
  else confidence = 'Low';

  return {
    trend,
    confidence,
    usableCount: usable.length,
    totalCount,
    formerAvg: Math.round(formerAvg),
    latterAvg: Math.round(latterAvg),
    deltaAbsolute: Math.round(deltaAbsolute),
    deltaPercent: Math.round(deltaPercent * 10) / 10,
    reason,
  };
}
